import { kml } from '@tmcw/togeojson';
import type { Feature, GeoJsonProperties, Geometry, GeometryCollection } from 'geojson';
import JSZip from 'jszip';

type ReturnPromise = Promise<{
  validObjects: Feature<Geometry | null, GeoJsonProperties>[];
  invalidObjects: Feature<Geometry | null, GeoJsonProperties>[];
}>;

export default async function fileToGeojson(e: React.ChangeEvent<HTMLInputElement>): ReturnPromise {
  // NOTE: wrapping this in a promise so we can return the result. The onload() method
  // is asynchronous in nature but you cannot 'await' it, so we are just resolving the
  // entire function and both arrays.
  return new Promise((resolve, reject) => {
    // Only execute if the file exists.
    if (!e.target.files) {
      alert('Could not find file.');
      reject();
    }

    const file = e.target.files![0];
    const reader = new FileReader();

    // Two drawing object arrays: one for good ones and ones that don't contain valid data.
    const validObjects: Feature<Geometry | null, GeoJsonProperties>[] = [];
    const invalidObjects: Feature<Geometry | null, GeoJsonProperties>[] = [];

    // This converts the uploaded file to text/xml and invoked the reader.onload() method below.
    reader.readAsText(file);

    // this sorts each feature, it is called in the reader.onload() method below.
    function sortGeojsonData(feature: Feature<Geometry | null, GeoJsonProperties>) {
      const gType = feature.geometry?.type;
      // if the feature's geometry does not exist, it's a bad object.
      if (feature.geometry === null) invalidObjects.push(feature);
      // If the geometry is a feature collection, the nested objects
      // will be filtered in the helper function.
      else if (gType === 'GeometryCollection') {
        sortGeometryCollection(feature.geometry, feature.properties);
      }
      // If the object is any of these types, we can't render them because CivilGrid
      // does not currently support them.
      else if (gType === 'MultiLineString' || gType === 'MultiPoint' || gType === 'MultiPolygon') {
        invalidObjects.push(feature);
      } else {
        validObjects.push(feature);
      }
    }

    function sortGeometryCollection(geometryCollection: GeometryCollection, properties: GeoJsonProperties) {
      const { geometries } = geometryCollection;
      for (let i = 0; i < geometries.length; i++) {
        const gType = geometries[i].type;
        // if the passed in GeometryCollection contains a nested GeometryCollection,
        // recursively call itself until the filtering is done.
        if (gType === 'GeometryCollection') {
          return sortGeometryCollection(geometries[i] as GeometryCollection, properties);
        } else if (
          // CivilGrid does not support these types at the moment.
          gType === 'MultiLineString' ||
          gType === 'MultiPoint' ||
          gType === 'MultiPolygon'
        ) {
          // Have to create a feature to maintain the current shape.
          const feature: Feature<Geometry, GeoJsonProperties> = {
            type: 'Feature',
            geometry: geometries[i],
            properties,
          };

          invalidObjects.push(feature);
        } else {
          // Have to create a feature to maintain the current shape.
          const feature: Feature<Geometry, GeoJsonProperties> = {
            type: 'Feature',
            geometry: geometries[i],
            properties,
          };

          validObjects.push(feature);
        }
      }
    }

    // fires when the file is loaded and has been read.
    reader.onload = async (e) => {
      let geojson: GeoJSON.FeatureCollection<Geometry | null> | undefined;

      if (file.name.endsWith('.kml')) {
        const kmlContent = e.target?.result;
        const kmlData = new DOMParser().parseFromString(kmlContent as string, 'text/xml');

        geojson = kml(kmlData);
      } else if (file.name.endsWith('.kmz')) {
        const zip = await JSZip.loadAsync(file);

        const kmlContent = await zip.file('doc.kml')?.async('text');
        const kmlData = new DOMParser().parseFromString(kmlContent as string, 'text/xml');

        geojson = kml(kmlData);
      }

      if (geojson?.type !== 'FeatureCollection') return alert('Error during data import. Invalid data set.');

      for (let i = 0; i < geojson.features.length; i++) {
        sortGeojsonData(geojson.features[i]);
      }

      const result = {
        validObjects,
        invalidObjects,
      };

      resolve(result);
    };
  });
}
